// Form Builder Types - Reusable across all modules
export interface FormField {
  id: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  required: boolean;
  position: {
    row: number;
    col: number;
    colSpan?: number;
  };
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
  options?: string[]; // For select/radio/checkbox
  defaultValue?: any;
  helpText?: string;
}

export interface FormLayout {
  columns: 2 | 3;
  fields: FormField[];
}

export interface Form {
  id?: string;
  name: string;
  description?: string;
  layout: FormLayout;
  isTemplate: boolean;
}

export type FieldType =
  // Text Inputs
  | 'text'
  | 'textarea'
  | 'richtext'
  | 'number'
  | 'decimal'
  | 'currency'
  | 'email'
  | 'phone'
  | 'url'
  | 'password'
  
  // Date & Time
  | 'date'
  | 'time'
  | 'datetime'
  | 'daterange'
  
  // Choice Fields
  | 'select'
  | 'multiselect'
  | 'radio'
  | 'checkbox'
  | 'switch'
  
  // Specialized
  | 'rating'
  | 'slider'
  | 'color'
  | 'file'
  | 'image'
  | 'signature'
  
  // Location
  | 'location'
  | 'country'
  | 'state'
  
  // Display Only
  | 'heading'
  | 'divider'
  | 'html';

export interface FieldTypeDefinition {
  type: FieldType;
  label: string;
  icon: string;
  defaultLabel: string;
  hasOptions: boolean;
  category: 'text' | 'number' | 'datetime' | 'choice' | 'special' | 'location' | 'display';
  description: string;
}

export const FIELD_TYPES: FieldTypeDefinition[] = [
  // TEXT INPUTS
  {
    type: 'text',
    label: 'Short Text',
    icon: 'FontSizeOutlined',
    defaultLabel: 'Text Field',
    hasOptions: false,
    category: 'text',
    description: 'Single line text input',
  },
  {
    type: 'textarea',
    label: 'Long Text',
    icon: 'AlignLeftOutlined',
    defaultLabel: 'Long Text',
    hasOptions: false,
    category: 'text',
    description: 'Multi-line text area',
  },
  {
    type: 'richtext',
    label: 'Rich Text Editor',
    icon: 'EditOutlined',
    defaultLabel: 'Rich Text',
    hasOptions: false,
    category: 'text',
    description: 'Formatted text with WYSIWYG editor',
  },
  {
    type: 'email',
    label: 'Email',
    icon: 'MailOutlined',
    defaultLabel: 'Email Address',
    hasOptions: false,
    category: 'text',
    description: 'Email address with validation',
  },
  {
    type: 'phone',
    label: 'Phone',
    icon: 'PhoneOutlined',
    defaultLabel: 'Phone Number',
    hasOptions: false,
    category: 'text',
    description: 'Phone number input',
  },
  {
    type: 'url',
    label: 'Website URL',
    icon: 'LinkOutlined',
    defaultLabel: 'Website',
    hasOptions: false,
    category: 'text',
    description: 'URL with validation',
  },
  {
    type: 'password',
    label: 'Password',
    icon: 'LockOutlined',
    defaultLabel: 'Password',
    hasOptions: false,
    category: 'text',
    description: 'Masked password input',
  },
  
  // NUMBER INPUTS
  {
    type: 'number',
    label: 'Number',
    icon: 'NumberOutlined',
    defaultLabel: 'Number',
    hasOptions: false,
    category: 'number',
    description: 'Integer number input',
  },
  {
    type: 'decimal',
    label: 'Decimal',
    icon: 'CalculatorOutlined',
    defaultLabel: 'Decimal Number',
    hasOptions: false,
    category: 'number',
    description: 'Decimal/float number',
  },
  {
    type: 'currency',
    label: 'Currency',
    icon: 'DollarOutlined',
    defaultLabel: 'Amount',
    hasOptions: false,
    category: 'number',
    description: 'Currency amount with symbol',
  },
  {
    type: 'slider',
    label: 'Slider',
    icon: 'SliderOutlined',
    defaultLabel: 'Slider',
    hasOptions: false,
    category: 'number',
    description: 'Numeric slider with range',
  },
  
  // DATE & TIME
  {
    type: 'date',
    label: 'Date',
    icon: 'CalendarOutlined',
    defaultLabel: 'Date',
    hasOptions: false,
    category: 'datetime',
    description: 'Date picker',
  },
  {
    type: 'time',
    label: 'Time',
    icon: 'ClockCircleOutlined',
    defaultLabel: 'Time',
    hasOptions: false,
    category: 'datetime',
    description: 'Time picker',
  },
  {
    type: 'datetime',
    label: 'Date & Time',
    icon: 'FieldTimeOutlined',
    defaultLabel: 'Date & Time',
    hasOptions: false,
    category: 'datetime',
    description: 'Date and time picker',
  },
  {
    type: 'daterange',
    label: 'Date Range',
    icon: 'CalendarOutlined',
    defaultLabel: 'Date Range',
    hasOptions: false,
    category: 'datetime',
    description: 'Start and end date picker',
  },
  
  // CHOICE FIELDS
  {
    type: 'select',
    label: 'Dropdown',
    icon: 'DownOutlined',
    defaultLabel: 'Select Option',
    hasOptions: true,
    category: 'choice',
    description: 'Single select dropdown',
  },
  {
    type: 'multiselect',
    label: 'Multi-Select',
    icon: 'SelectOutlined',
    defaultLabel: 'Select Multiple',
    hasOptions: true,
    category: 'choice',
    description: 'Multiple selection dropdown',
  },
  {
    type: 'radio',
    label: 'Radio Buttons',
    icon: 'CheckCircleOutlined',
    defaultLabel: 'Choose One',
    hasOptions: true,
    category: 'choice',
    description: 'Single choice radio buttons',
  },
  {
    type: 'checkbox',
    label: 'Checkboxes',
    icon: 'CheckSquareOutlined',
    defaultLabel: 'Select All That Apply',
    hasOptions: true,
    category: 'choice',
    description: 'Multiple choice checkboxes',
  },
  {
    type: 'switch',
    label: 'Toggle Switch',
    icon: 'BulbOutlined',
    defaultLabel: 'Yes/No',
    hasOptions: false,
    category: 'choice',
    description: 'Boolean on/off switch',
  },
  
  // SPECIALIZED
  {
    type: 'rating',
    label: 'Star Rating',
    icon: 'StarOutlined',
    defaultLabel: 'Rating',
    hasOptions: false,
    category: 'special',
    description: 'Star rating (1-5)',
  },
  {
    type: 'color',
    label: 'Color Picker',
    icon: 'BgColorsOutlined',
    defaultLabel: 'Color',
    hasOptions: false,
    category: 'special',
    description: 'Color selection',
  },
  {
    type: 'file',
    label: 'File Upload',
    icon: 'UploadOutlined',
    defaultLabel: 'Upload File',
    hasOptions: false,
    category: 'special',
    description: 'File upload (any type)',
  },
  {
    type: 'image',
    label: 'Image Upload',
    icon: 'PictureOutlined',
    defaultLabel: 'Upload Image',
    hasOptions: false,
    category: 'special',
    description: 'Image upload with preview',
  },
  {
    type: 'signature',
    label: 'Signature',
    icon: 'EditOutlined',
    defaultLabel: 'Signature',
    hasOptions: false,
    category: 'special',
    description: 'Digital signature pad',
  },
  
  // LOCATION
  {
    type: 'location',
    label: 'Location/Address',
    icon: 'EnvironmentOutlined',
    defaultLabel: 'Address',
    hasOptions: false,
    category: 'location',
    description: 'Full address with autocomplete',
  },
  {
    type: 'country',
    label: 'Country',
    icon: 'GlobalOutlined',
    defaultLabel: 'Country',
    hasOptions: false,
    category: 'location',
    description: 'Country selector',
  },
  {
    type: 'state',
    label: 'State/Province',
    icon: 'EnvironmentOutlined',
    defaultLabel: 'State',
    hasOptions: false,
    category: 'location',
    description: 'State/province selector',
  },
  
  // DISPLAY ONLY
  {
    type: 'heading',
    label: 'Heading',
    icon: 'FontSizeOutlined',
    defaultLabel: 'Section Heading',
    hasOptions: false,
    category: 'display',
    description: 'Section heading text',
  },
  {
    type: 'divider',
    label: 'Divider',
    icon: 'LineOutlined',
    defaultLabel: 'Divider',
    hasOptions: false,
    category: 'display',
    description: 'Visual separator line',
  },
  {
    type: 'html',
    label: 'HTML Content',
    icon: 'CodeOutlined',
    defaultLabel: 'Custom HTML',
    hasOptions: false,
    category: 'display',
    description: 'Custom HTML content',
  },
];

// Group fields by category for organized palette
export const getFieldsByCategory = () => {
  const categories: Record<string, FieldTypeDefinition[]> = {
    text: [],
    number: [],
    datetime: [],
    choice: [],
    special: [],
    location: [],
    display: [],
  };

  FIELD_TYPES.forEach((field) => {
    categories[field.category].push(field);
  });

  return categories;
};
